import { EventTypes, ROOT_NODE } from '@candulabs/craft';
import { styled } from 'canoe';
import React, { useEffect } from 'react';

import { DocumentGrid } from 'craft/containers/Craft/DocumentGrid';
import { VIEWPORT_CONTAINER_ID, VIEWPORT_CONTENT_ID } from 'craft/containers/Viewport/constants';
import { useCraftViewportEventHandlers } from 'craft/containers/Viewport/modes/hooks/useCraftViewportEventHandlers';
import { useEditor } from 'craft/hooks';
import { useCurrentSnapshot, useToolboxTab, usePlacementMode, useViewportBreakpoint } from 'craft/state';
import { ActiveSnapshot } from 'dashboard/pages/Content/pages/Snapshots';

import { ViewportContainer } from './components';
import { SnapshotLoader } from './components/SnapshotLoader';

export interface SnapshotViewportProps {
  children: React.ReactNode;
  contentId: string;
  rootNodeName: string;
  isRootSelected: boolean;
  editIterator: number;
}

export const InlineContent = styled.div`
  z-index: 0;
  position: absolute;
  flex-grow: 1;
  flex-direction: column;
`;

/**
 * Renders the editor with Snapshots
 */
export const InlineSnapshotViewport = React.memo(
  ({ children, contentId, rootNodeName, isRootSelected, editIterator }: SnapshotViewportProps) => {
    const viewportBreakpoint = useViewportBreakpoint();
    const { containerRef, contentRef } = useCraftViewportEventHandlers();

    const { placementMode } = usePlacementMode();
    const { isSnapshotLoaded } = useCurrentSnapshot();
    const { forcePlacementsTab } = useToolboxTab();
    const { actions } = useEditor();

    useEffect(() => {
      // when we are actively editing an Inline placement , select the document to show the outline
      if (forcePlacementsTab) {
        actions.setEventNodes(EventTypes.selected, ROOT_NODE);
      }
    }, [forcePlacementsTab]);

    if (placementMode) {
      // if we're just in placement mode we only need the snapshot
      // but we should render with a consistent key to prevent re-fetching everything
      return (
        <ViewportContainer
          id={VIEWPORT_CONTAINER_ID}
          viewport={viewportBreakpoint}
          ref={(dom: any) => {
            containerRef.current = dom;
          }}
          data-cy="editor-viewport"
          isFullWidthSnapshot
        >
          <ActiveSnapshot
            rootNodeName={rootNodeName}
            key={`snapshot-${contentId}`}
            syncIterator={editIterator}
          />
        </ViewportContainer>
      );
    }

    return (
      <ViewportContainer
        id={VIEWPORT_CONTAINER_ID}
        viewport={viewportBreakpoint}
        ref={(dom: any) => {
          containerRef.current = dom;
        }}
        data-cy="editor-viewport"
        isFullWidthSnapshot
      >
        <ActiveSnapshot
          rootNodeName={rootNodeName}
          key={`snapshot-${contentId}`}
          syncIterator={editIterator}
        />
        {!isSnapshotLoaded ? (
          <SnapshotLoader />
        ) : (
          <InlineContent
            id={VIEWPORT_CONTENT_ID}
            ref={(dom: any) => {
              contentRef.current = dom;
            }}
          >
            <DocumentGrid isSelected={!!isRootSelected} shouldHideLine />
            {children}
          </InlineContent>
        )}
      </ViewportContainer>
    );
  },
);
